import { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast';
import { api, extractErrorMessage } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { TIPO_MOVIMIENTO_LABEL, type TipoMovimiento } from '../../lib/tiposMovimiento';
import {
  A_CARGO_DE, A_CARGO_DE_LABEL, DATOS_QUE_PIDE, SENTIDO_LABEL, TIPOS_POR_SENTIDO, URL_MOTIVOS, comoLista, etiquetaACargoDe,
  type MarcaPide, type MotivoMovimiento, type SentidoMotivo,
} from '../../lib/motivosMovimiento';

interface FormMotivo extends Record<MarcaPide, boolean> {
  id: number | null;
  codigo: string;
  nombre: string;
  sentido: SentidoMotivo;
  tipo: TipoMovimiento;
  aCargoDe: string;
}

/** Qué pide el motivo, cada marca en true o false (las que faltan en la respuesta, false). */
const marcasDe = (m?: Partial<Record<MarcaPide, boolean>>) =>
  Object.fromEntries(DATOS_QUE_PIDE.map(d => [d.marca, !!m?.[d.marca]])) as Record<MarcaPide, boolean>;

const formVacio = (): FormMotivo => ({
  id: null, codigo: '', nombre: '', sentido: 'salida', tipo: 'BAJA_ROTURA', aCargoDe: '', ...marcasDe(),
});

/** Lo que pide el motivo al registrar, para la lista. */
const quePide = (m: MotivoMovimiento) => DATOS_QUE_PIDE.filter(d => m[d.marca]).map(d => d.nombre).join(', ') || 'Nada extra';

/**
 * Configuración → Mantenimiento → "Motivos de movimientos de stock" (la página la muestra sólo con wms:ver). Todo
 * registro manual de stock elige uno (obligatorio). Lo edita sólo admin con wms:editar (el backend lo exige igual). Los
 * motivos no se borran: se desactivan.
 */
export function MotivosMovimientoStock() {
  const { user, can } = useAuth();
  const esAdmin = user?.role === 'admin' && can('wms', 'editar');
  const toast = useToast();
  const { data: motivosResp, loading, error, refetch } = useApi<MotivoMovimiento[]>(`${URL_MOTIVOS}?incluirInactivos=true`);
  const motivos = comoLista<MotivoMovimiento>(motivosResp);
  const [form, setForm] = useState<FormMotivo | null>(null);
  const [formError, setFormError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    if (!form || guardando) return;
    if (!form.nombre.trim() || (form.id == null && !form.codigo.trim())) {
      setFormError('Completá el código y el nombre');
      return;
    }
    const datos = {
      nombre: form.nombre.trim(), sentido: form.sentido, tipo: form.tipo, aCargoDe: form.aCargoDe || null, ...marcasDe(form),
    };
    setGuardando(true);
    setFormError('');
    try {
      if (form.id == null) await api.post(URL_MOTIVOS, { codigo: form.codigo.trim(), ...datos });
      else await api.put(`${URL_MOTIVOS}/${form.id}`, datos);
      toast.addToast('Motivo guardado', 'success');
      setForm(null);
      refetch();
    } catch (e) {
      setFormError(extractErrorMessage(e));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarActivo = async (m: MotivoMovimiento) => {
    try {
      await api.post(`${URL_MOTIVOS}/${m.id}/${m.activo ? 'desactivar' : 'activar'}`, {});
      toast.addToast(m.activo ? `"${m.nombre}" desactivado` : `"${m.nombre}" activado`, 'success');
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const editar = (m: MotivoMovimiento) => {
    setFormError('');
    setForm({
      id: m.id, codigo: m.codigo, nombre: m.nombre, sentido: m.sentido, tipo: m.tipo, aCargoDe: m.aCargoDe ?? '', ...marcasDe(m),
    });
  };

  const cambiarSentido = (sentido: SentidoMotivo) =>
    setForm(f => (f ? { ...f, sentido, tipo: TIPOS_POR_SENTIDO[sentido].includes(f.tipo) ? f.tipo : TIPOS_POR_SENTIDO[sentido][0] } : f));

  return (
    <div className="space-y-3">
      {esAdmin && (
        <div className="flex justify-end">
          <Button onClick={() => { setFormError(''); setForm(formVacio()); }}>Nuevo motivo</Button>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Motivo</th>
              <th className="px-3 py-2">Sentido</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">A cargo de</th>
              <th className="px-3 py-2">Pide al registrar</th>
              <th className="px-3 py-2">Estado</th>
              {esAdmin && <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>}
            </tr>
          </thead>
          <tbody>
            {loading && motivos.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-slate-500">Cargando…</td></tr>
            )}
            {motivos.map(m => (
              <tr key={m.id} className={`border-t border-slate-100 ${m.activo ? '' : 'text-slate-400'}`}>
                <td className="px-3 py-2">
                  <span className="font-medium">{m.nombre}</span>
                  <span className="block text-xs text-slate-500">{m.codigo}</span>
                </td>
                <td className="px-3 py-2">{SENTIDO_LABEL[m.sentido] ?? m.sentido}</td>
                <td className="px-3 py-2">{TIPO_MOVIMIENTO_LABEL[m.tipo] ?? m.tipo}</td>
                <td className="px-3 py-2">{etiquetaACargoDe(m.aCargoDe)}</td>
                <td className="px-3 py-2">{quePide(m)}</td>
                <td className="px-3 py-2"><Badge tone={m.activo ? 'green' : 'slate'}>{m.activo ? 'Activo' : 'Inactivo'}</Badge></td>
                {esAdmin && (
                  <td className="px-3 py-2 whitespace-nowrap text-right space-x-2">
                    <button type="button" className="text-brand-700 hover:underline" onClick={() => editar(m)}>Editar</button>
                    <button type="button" className="text-slate-600 hover:underline" onClick={() => cambiarActivo(m)}>
                      {m.activo ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={form !== null} onClose={() => setForm(null)} title={form?.id == null ? 'Nuevo motivo' : 'Editar motivo'} size="sm">
        {form && (
          <div className="space-y-3">
            <div>
              <label htmlFor="motivo-codigo" className="block text-sm font-medium mb-1">Código</label>
              <input
                id="motivo-codigo"
                className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-100"
                value={form.codigo}
                disabled={form.id != null}
                placeholder="Ej.: ROBO_DEPOSITO"
                onChange={e => setForm({ ...form, codigo: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Identifica al motivo: no se cambia después.</p>
            </div>
            <div>
              <label htmlFor="motivo-nombre" className="block text-sm font-medium mb-1">Nombre</label>
              <input id="motivo-nombre" className="w-full px-3 py-2 border rounded-lg" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="motivo-sentido" className="block text-sm font-medium mb-1">Sentido</label>
                <select id="motivo-sentido" className="w-full px-3 py-2 border rounded-lg" value={form.sentido} onChange={e => cambiarSentido(e.target.value as SentidoMotivo)}>
                  <option value="salida">Salida</option>
                  <option value="entrada">Entrada</option>
                </select>
              </div>
              <div>
                <label htmlFor="motivo-tipo" className="block text-sm font-medium mb-1">Tipo</label>
                <select id="motivo-tipo" className="w-full px-3 py-2 border rounded-lg" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value as TipoMovimiento })}>
                  {TIPOS_POR_SENTIDO[form.sentido].map(t => <option key={t} value={t}>{TIPO_MOVIMIENTO_LABEL[t]}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="motivo-acargo" className="block text-sm font-medium mb-1">A cargo de</label>
              <select id="motivo-acargo" className="w-full px-3 py-2 border rounded-lg" value={form.aCargoDe} onChange={e => setForm({ ...form, aCargoDe: e.target.value })}>
                <option value="">Sin asignar</option>
                {A_CARGO_DE.map(a => <option key={a} value={a}>{A_CARGO_DE_LABEL[a]}</option>)}
              </select>
            </div>
            <fieldset className="space-y-1.5">
              <legend className="text-sm font-medium mb-1">Al registrar un movimiento con este motivo</legend>
              {DATOS_QUE_PIDE.map(d => (
                <label key={d.marca} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form[d.marca]} onChange={e => setForm({ ...form, [d.marca]: e.target.checked })} />
                  {d.casilla}
                </label>
              ))}
              <p className="text-xs text-slate-500">Lo que marques es obligatorio en el formulario y se pide sólo con este motivo.</p>
            </fieldset>
            {formError && <p className="text-red-600 text-sm">{formError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-sm">Cancelar</button>
              <Button onClick={guardar} loading={guardando}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
